/* eslint-disable arrow-parens */
import { useState, useCallback, useEffect, useContext, useRef, Suspense } from "react";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { AuthContext, UIContext } from "../../../../../contexts";
import a11yScrollableProps from "../../../../../constants/tabPanelProps/ayScrollableProps";
import ScrollTabPanel from "../../../../shared/tabPanelComponent/ScrollTabPanel";

import { Box, Tab, Tabs, Badge, withStyles } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Enrollments from "./enrollments/Enrollments";
import Benefits from "./benefits/Benefits";
import EmploymentDetails from "./employmentDetails/EmploymentDetails";
import MemberFamilyDetails from "./memeberFamilyDetails/MemberFamilyDetails";
import NoteDetails from "./noteDetails/NoteDetails";
import History from "./memberHistory/History";
import initCapitalize from "../../../../../utils/commonFunctions/initCapitalize";
import { HistoryContactInformation, HistoryProfileInformation, Member } from "../../../../../@types/member.types";
import useRouteParam from "../../../../../hooks/useRouteParam";
import ExtractRouteParams from "../../../../../utils/commonFunctions/extractRouteParams";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../utils/api";
import ROLES from "../../../../../constants/roles";
import { Paycheck } from "../../../../../@types/paycheck.types";
import {
	LazyVEBBenefits,
	LazyEnrollments,
	LazyMemberFamilyDetails,
	LazyClaimDetails,
	LazyBenefits,
	LazyEmploymentDetails,
	LazyNoteDetails,
	LazyHistoryDetails,
	LazyOpenEnrollmentLists,
	LazyMemberSettings,
	LazyMemberLogs,
	LazySendText,
	LazyDocumentSharing,
	LazyClaimV2DetailsGrid
} from "../../..";
import ClaimList from "./claim/claimList/ClaimList";
import { useEnroller } from "../../../../../hooks";
import { Role } from "../../../../../@types/role.types";
import { GROUP_NUMBER } from "../../../../../constants/groups";
import BulkText from "../../../admin/bulkText/BulkText";
import { TabIndex } from "../../../../../constants/tabPanelProps/tabIndex";
import { clearConfigCache } from "prettier";
import isEmployerRoute from "../../../../../utils/commonFunctions/isEmployerRoute";
import { getPermissionsFromGroupOwner } from "../../../../../utils/getPermissionsFromGroupOwner";
import { _ } from "ag-grid-community";
import { assignAllGroups } from "../../../../../utils/api/admin/healthLinks/healthLinks";
import CircularLoader from "../../../../shared/loader/CircularLoader";

const StyledBadge = withStyles((theme) => ({
	badge: {
		backgroundColor: "#b8ddc0",
		color: "#558b2f",
		fontWeight: "bold",
		// boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		top: 3,
		left: -15,
		"&::after": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			borderRadius: "50%",
			// animation: "$ripple 1.2s infinite ease-in-out",
			// border: "1px solid currentColor",
			// eslint-disable-next-line quotes
			content: '""'
		}
	},
	"@keyframes ripple": {
		"0%": {
			transform: "scale(.8)",
			opacity: 1
		},
		"100%": {
			transform: "scale(2.4)",
			opacity: 0
		}
	}
}))(Badge);

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
		// backgroundColor: theme.palette.background.paper
	}
}));

const MemberDetails = (): JSX.Element => {
	const classes = useStyles();
	const { isEnroller } = useEnroller();
	const [value, setValue] = useState(0);
	const { user } = useContext(AuthContext);
	const { setDashboardHeader, setMemberUI } = useContext(UIContext);
	const querySSN = useRouteParam("qs");
	const queryTo = useRouteParam("to");
	const queryTypeTo = useRouteParam("type");
	const queryGroupNumber = useRouteParam("gn");
	const routeParam = ExtractRouteParams(); // This is to extract the route after ?(Question Mark)
	const [employee, setEmployee] = useState<Member>();
	const [familyInformation, setFamilyInformation] = useState<Member[]>([]);
	const [historyFamilyInformation, setHistoryFamilyInformation] = useState<Member[]>([]);
	const [activePaycheck, setActivePaycheck] = useState<Paycheck>();
	const [historyProfileInformation, setHistoryProfileInformation] = useState<HistoryProfileInformation[]>([]);
	const [historyContactInformation, setHistoryContactInformation] = useState<HistoryContactInformation[]>([]);
	const [historyEmployments, setHistoryEmployments] = useState<Paycheck[]>([]);
	const [historyPaychecks, setHistoryPaychecks] = useState<Paycheck[]>([]);
	const [countNotes, setCountNotes] = useState<number | null>(null);
	const [selectedGroup, setSelectedGroup] = useState<any>();

	const [tabsAndPanels, setTabsAndPanels] = useState<any[]>([]);
	const [paycheckByEmployeeSSNLoader, setPaycheckByEmployeeSSNLoader] = useState(true);
	const [membersByEmployeeSSNLoader, setMembersByEmployeeSSNLoader] = useState(true);
	const [countNotesForEachMemberLoader, setCountNotesForEachMemberLoader] = useState(true);
	const [assignedGroupsAndLocationsLoader, setAssignedGroupsAndLocationsLoader] = useState(true);
	// console.log("employee", employee);
	const handleLabelChange = (event: any, value: number) => {
		const clickedTabName: string = event.target.innerText;
		setValue(value);
	};

	const membersDetailsTabPermission =
		user && isEmployerRoute() && queryGroupNumber
			? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.employer_members_menu.self
					.members_list_table.member_details_view
			: user
			? getPermissionsFromGroupOwner(user, 0)?.members_menu.self.members_list_table.member_details_view
			: null;

	const getCountNotesForEachMember = useCallback(async (group_number: number, SSN: string) => {
		setCountNotesForEachMemberLoader(true);
		try {
			const findNotesQuery = {
				group_number,
				SSN
			};

			const response: number = await api.notes.getCountNotesForEachMember(findNotesQuery);

			if (response) {
				setCountNotes(response);
			}
		} catch (error) {
			console.log("Error fetching CountNotesForEachMember", error);
		} finally {
			setCountNotesForEachMemberLoader(false);
		}
	}, []);

	const getMembersByEmployeeSSN = useCallback(
		async (_employee_SSN: string, group_number: number) => {
			setMembersByEmployeeSSNLoader(true);
			try {
				const findEmployeeQuery = {
					group_number,
					employee_SSN: _employee_SSN
				};

				const response: any = await api.member.getMembersByEmployeeSSN(findEmployeeQuery);
				// console.log("Members: ", members);
				if (response) {
					// console.log("Members + history: ", response);
					const _familyInformation: Member[] = [];
					const {
						memberInstance,
						historyFamilyInformationInstance,
						historyProfileInformation,
						historyContactInformation
					} = response;

					setHistoryProfileInformation(Object.assign([], historyProfileInformation));
					setHistoryContactInformation(Object.assign([], historyContactInformation));
					setHistoryFamilyInformation(Object.assign([], historyFamilyInformationInstance));

					memberInstance.forEach((member: Member) => {
						const { role } = member;

						if (role === ROLES.employee) {
							setEmployee(Object.assign({}, member));
							setMemberUI(
								Object.assign(
									{},
									{
										firstName: member?.first_name,
										lastName: member?.last_name,
										role: member?.role as "EMPLOYEE" | "DEPENDENT",
										employeeId: member?.member_id
									}
								)
							);
						}
						if (role === ROLES.dependent) {
							_familyInformation.push(member as Member);
						}
					});

					if (_familyInformation) setFamilyInformation(Object.assign([], _familyInformation));
				}
			} catch (error) {
				console.log("Error fetching MembersByEmployeeSSN", error);
			} finally {
				setMembersByEmployeeSSNLoader(false);
			}
		},
		[setMemberUI]
	);

	const getPaycheckByEmployeeSSN = useCallback(async (employee_SSN: string, group_number: number) => {
		setPaycheckByEmployeeSSNLoader(true);
		try {
			const findEmployeeAndPaycheckQuery = {
				employee_SSN,
				group_number
			};
			const paychecks = await api.paycheck.getPaycheckByEmployeeSSN(findEmployeeAndPaycheckQuery);
			// console.log("Paychecks: ", paychecks);
			if (paychecks) {
				const _historyEmploymentInformation: Paycheck[] = [];
				const _historyPaycheckInformation: Paycheck[] = [];
				const { activePaycheck, historyPaychecks } = paychecks;

				setActivePaycheck(Object.assign({}, activePaycheck));

				historyPaychecks.forEach((paycheck: Paycheck) => {
					const { employment_status, paycheck_status } = paycheck;

					if (employment_status === "ENDED" || employment_status === "TERM")
						_historyEmploymentInformation.push(paycheck as Paycheck);

					if (employment_status === "ACTIVE" && paycheck_status === "ENDED")
						_historyPaycheckInformation.push(paycheck as Paycheck);
				});

				setHistoryEmployments(Object.assign([], _historyEmploymentInformation));
				setHistoryPaychecks(Object.assign([], _historyPaycheckInformation));
			}
		} catch (error) {
			console.log("Error fetching paycheck by employee SSN:", error);
		} finally {
			setPaycheckByEmployeeSSNLoader(false);
		}
	}, []);
	const getAssignGroups = useCallback(async () => {
		setAssignedGroupsAndLocationsLoader(true);
		try {
			const params = {
				user_id: user?._id
			};
			const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

			if (response) {
				const _assignedGroups: any[] = [];
				response.groupsAndLocations.forEach((group: any) => {
					if (group.assigned_group_info) {
						_assignedGroups.push(group);
					}
				});

				// setAssignedGroups(Object.assign([], _assignedGroups));
				_assignedGroups.forEach((group: any) => {
					// This condition is to check assigned group with the parameter group
					if (group.group_number === queryGroupNumber) {
						setSelectedGroup(Object.assign({}, group));
					}
				});
			}
		} catch (error) {
			console.log("Error fetching AssignedGroupsAndLocations", error);
		} finally {
			setAssignedGroupsAndLocationsLoader(false);
		}
	}, [user?._id, queryGroupNumber]);

	// console.log("Active Paychecks", activePaycheck);
	// console.log("History Employments", historyEmployments);
	// console.log("History Paychecks", historyPaychecks);
	// console.log("Count Notes", countNotes);

	useEffect(() => {
		if (queryTypeTo && queryTypeTo === "veb" && queryTo === "benefits") {
			setValue(3);
		} else if (queryTo && queryTo === "benefits") {
			setValue(2);
		}

		setDashboardHeader(ADMIN_DASHBOARD_HEADER.member_details);
		getAssignGroups();
	}, [queryTo, queryTypeTo, setDashboardHeader, getAssignGroups]);

	useEffect(() => {
		if (querySSN && queryGroupNumber) {
			getMembersByEmployeeSSN(querySSN as string, queryGroupNumber as number);
			getPaycheckByEmployeeSSN(querySSN as string, queryGroupNumber as number);
			getCountNotesForEachMember(queryGroupNumber as number, querySSN as string);
			// handleTabsAndPanelsRender();
		}
	}, [getCountNotesForEachMember, getMembersByEmployeeSSN, getPaycheckByEmployeeSSN, queryGroupNumber, querySSN]);
	useEffect(() => {
		if (membersDetailsTabPermission && employee) {
			const tabs = [];
			const panels = [];
			const generatedTabs: any = [];
			if (membersDetailsTabPermission.member_and_family_tab.view) {
				generatedTabs.push({
					label: "Member & Family",
					component:
						employee !== undefined && familyInformation !== undefined ? (
							<Suspense fallback={<div />}>
								<LazyMemberFamilyDetails
									employee={employee}
									familyInformation={familyInformation}
									selectedGroup={selectedGroup}
								/>
							</Suspense>
						) : null
				});
			}
			if (membersDetailsTabPermission.employment_tab.view) {
				generatedTabs.push({
					label: "Employment",
					component:
						employee !== undefined && activePaycheck !== undefined ? (
							<Suspense fallback={<div />}>
								<LazyEmploymentDetails
									employee={employee}
									paycheck={activePaycheck}
									selectedGroup={selectedGroup}
								/>
							</Suspense>
						) : null
				});
			}
			if (membersDetailsTabPermission.core_benefits_tab.view) {
				generatedTabs.push({
					label: "Core Benefits",
					component:
						employee !== undefined ? (
							<Suspense fallback={<div />}>
								<LazyBenefits employee={employee} />
							</Suspense>
						) : null
				});
			}
			if (membersDetailsTabPermission.veb_benefits_tab.view) {
				generatedTabs.push({
					label: "VEB Benefits",
					component:
						employee !== undefined ? (
							<Suspense fallback={<div />}>
								<LazyVEBBenefits employee={employee} />
							</Suspense>
						) : null
				});
			}
			if (membersDetailsTabPermission.documents_tab.view) {
				generatedTabs.push({
					label: "Documents",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazyDocumentSharing employee={employee} />
						</Suspense>
					) : null
				});
			}
			if (membersDetailsTabPermission.open_enrollment_lists_tab.view) {
				generatedTabs.push({
					label: "Open Enrollments Lists",
					component:
						employee !== undefined ? (
							<Suspense fallback={<div />}>
								<LazyOpenEnrollmentLists employee={employee} />
							</Suspense>
						) : null
				});
			}
			if (membersDetailsTabPermission.claims_tab.view) {
				generatedTabs.push({
					label: "Claims",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazyClaimV2DetailsGrid />
						</Suspense>
					) : null
				});
			}
			if (membersDetailsTabPermission.notes_tab.view) {
				generatedTabs.push({
					label: "Notes",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazyNoteDetails member={employee} setCountNotes={setCountNotes} />
						</Suspense>
					) : null
				});
			}
			if (membersDetailsTabPermission.history_tab.view) {
				generatedTabs.push({
					label: "History",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazyHistoryDetails
								historyProfileInformation={historyProfileInformation}
								historyFamilyInformation={historyFamilyInformation}
								historyContactInformation={historyContactInformation}
								historyEmployments={historyEmployments}
								historyPaychecks={historyPaychecks}
								employee={employee}
							/>
						</Suspense>
					) : null
				});
			}
			if (membersDetailsTabPermission.member_settings_tab.view) {
				generatedTabs.push({
					label: "Member Settings",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazyMemberSettings member={employee} />
						</Suspense>
					) : null
				});
			}
			if (membersDetailsTabPermission.member_logs_tab.view) {
				generatedTabs.push({
					label: "Member Logs",
					component: (
						<Suspense fallback={<div />}>
							<LazyMemberLogs />
						</Suspense>
					)
				});
			}
			if (membersDetailsTabPermission.text_sending_tab.view) {
				generatedTabs.push({
					label: "Text Sending",
					component: employee ? (
						<Suspense fallback={<div />}>
							<LazySendText member={employee} />
						</Suspense>
					) : null
				});
			}

			setTabsAndPanels(generatedTabs);
		}

		// console.log(tabsAndPanels, "tablists", tabsAndPanels);
	}, [
		membersDetailsTabPermission,
		familyInformation,
		employee,
		historyProfileInformation,
		historyFamilyInformation,
		historyContactInformation,
		historyEmployments,
		historyPaychecks
	]);

	const isLoading =
		paycheckByEmployeeSSNLoader ||
		membersByEmployeeSSNLoader ||
		countNotesForEachMemberLoader ||
		assignedGroupsAndLocationsLoader;
	// if (!employee) {
	// 	return <></>;
	// }
	return (
		<>
			{isLoading && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
						backgroundColor: "rgba(255, 255, 255, 0.5)"
					}}
				>
					<CircularLoader />
				</div>
			)}
			{!isLoading && !employee ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: "40px"
					}}
				>
					Employee information not found.
				</div>
			) : (
				<div className="member-details-container" id="member-details-container">
					<div className="member-details-tab-panel-container" style={{ width: "auto" }}>
						<div className={classes.root + " glb-tab-panel-container"}>
							<Box sx={{ width: "100%" }}>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
									<Tabs
										value={value}
										onChange={handleLabelChange}
										TabIndicatorProps={{
											style: {
												border: "none",
												backgroundColor: "#558b2f"
											}
										}}
										variant="scrollable"
										scrollButtons="auto"
										aria-label="scrollable auto tabs example"
										className="glb-tab-panel"
									>
										{tabsAndPanels.map((tabAndPanel: any, index: number) => (
											<Tab
												label={tabAndPanel.label}
												key={index + tabAndPanel.label}
												value={index}
												className="glb-tab-label"
												{...a11yScrollableProps(index)}
											/>
										))}
									</Tabs>
									{tabsAndPanels.map((tabAndPanel: any, index: number) => (
										<ScrollTabPanel key={index} value={value} index={index}>
											{tabAndPanel.component}
										</ScrollTabPanel>
									))}
									{/* <Tab
									label={"Member & Family"}
									value={TabIndex["Member & Family"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Member & Family"])}
								/>
								<Tab
									label={"Employment"}
									value={TabIndex["Employment"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Employment"])}
								/>
								<Tab
									label={"Core Benefits"}
									value={TabIndex["Core Benefits"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Core Benefits"])}
								/>
								<Tab
									label={"VEB Benefits"}
									value={TabIndex["VEB Benefits"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["VEB Benefits"])}
								/>
								{/* <Tab label={"Enrollments"} className="glb-tab-label" {...a11yScrollableProps(TabIndex["Documents"])} /> */}
									{/*<Tab
									label={"Documents"}
									value={TabIndex["Documents"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Documents"])}
								/>
								<Tab
									label={"Open Enrollments Lists"}
									value={TabIndex["Open Enrollments Lists"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Open Enrollments Lists"])}
								/>
								{isEnroller ||
								(user?.role === ROLES.employer && user?.is_claim_visibility === false) ? null : (
									<Tab
										label={"Claims"}
										value={TabIndex["Claims"]}
										className="glb-tab-label"
										{...a11yScrollableProps(TabIndex["Claims"])}
									/>
								)}
								<Tab
									label={
										countNotes ? (
											<StyledBadge
												anchorOrigin={{
													vertical: "top",
													horizontal: "left"
												}}
												variant="standard"
												badgeContent={countNotes ? countNotes : ""}
											>
												Notes
											</StyledBadge>
										) : (
											"Notes"
										)
									}
									value={TabIndex["Notes"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Notes"])}
								/>
								<Tab
									label={"History"}
									value={TabIndex["History"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["History"])}
								/>

								{isClaimPageVisibleBySuperAdmin ? (
									<Tab
										label={"Claim-V2"}
										value={TabIndex["Claim-V2"]}
										className="glb-tab-label"
										{...a11yScrollableProps(TabIndex["Claim-V2"])}
									/>
								) : null}

								<Tab
									label={"Member Settings"}
									value={TabIndex["Member Settings"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Member Settings"])}
								/>

								<Tab
									label={"Member Logs"}
									value={TabIndex["Member Logs"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Member Logs"])}
								/>

								<Tab
									label={"Text Sending"}
									value={TabIndex["Text Sending"]}
									className="glb-tab-label"
									{...a11yScrollableProps(TabIndex["Text Sending"])}
								/> */}
									{/* </Tabs> */}
									{/* <ScrollTabPanel value={value} index={TabIndex["Member & Family"]}>
								{employee !== undefined && familyInformation !== undefined ? (
									<Suspense fallback={<div />}>
										<LazyMemberFamilyDetails
											employee={employee}
											familyInformation={familyInformation}
										/>
									</Suspense>
								) : null}
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["Employment"]}>
								{employee !== undefined && activePaycheck !== undefined ? (
									<Suspense fallback={<div />}>
										<LazyEmploymentDetails employee={employee} paycheck={activePaycheck} />
									</Suspense>
								) : null}
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["Core Benefits"]}>
								<Suspense fallback={<div />}>
									<LazyBenefits employee={employee} />
								</Suspense>
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["VEB Benefits"]}>
								<Suspense fallback={<div />}>
									<LazyVEBBenefits employee={employee} />
								</Suspense>
							</ScrollTabPanel>
							{/* <ScrollTabPanel value={value} index={TabIndex["Documents"]}>
									<Suspense fallback={<div />}>
										<LazyEnrollments employee={employee} />
									</Suspense>
								</ScrollTabPanel> */}
									{/* ================================================ (OPEN ENROLLMENT LIST - WIP) ================================= */}
									{/*<ScrollTabPanel value={value} index={TabIndex["Documents"]}>
								{employee ? (
									<Suspense fallback={<div />}>
										<LazyDocumentSharing employee={employee} />
									</Suspense>
								) : null}
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["Open Enrollments Lists"]}>
								{employee ? (
									<Suspense fallback={<div />}>
										<LazyOpenEnrollmentLists employee={employee} />
									</Suspense>
								) : null}
							</ScrollTabPanel>
							{isEnroller ||
							(user?.role === ROLES.employer && user?.is_claim_visibility === false) ? null : (
								<ScrollTabPanel value={value} index={TabIndex["Claims"]}>
									{employee ? (
										<Suspense fallback={<div />}>
											{/* <LazyClaimDetails member={employee} /> */}
									{/*<LazyClaimV2DetailsGrid />
										</Suspense>
									) : null}
								</ScrollTabPanel>
							)}
							<ScrollTabPanel value={value} index={TabIndex["Notes"]}>
								{employee ? (
									<Suspense fallback={<div />}>
										<LazyNoteDetails member={employee} setCountNotes={setCountNotes} />
									</Suspense>
								) : null}
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["History"]}>
								<Suspense fallback={<div />}>
									<LazyHistoryDetails
										historyProfileInformation={historyProfileInformation}
										historyFamilyInformation={historyFamilyInformation}
										historyContactInformation={historyContactInformation}
										historyEmployments={historyEmployments}
										historyPaychecks={historyPaychecks}
										employee={employee}
									/>
								</Suspense>
							</ScrollTabPanel>
							{isClaimPageVisibleBySuperAdmin ? (
								<ScrollTabPanel value={value} index={TabIndex["Claim-V2"]}>
									{employee ? (
										<Suspense fallback={<div />}>
											<LazyClaimV2DetailsGrid />
										</Suspense>
									) : null}
								</ScrollTabPanel>
							) : null}
							<ScrollTabPanel value={value} index={TabIndex["Member Settings"]}>
								{employee ? (
									<Suspense fallback={<div />}>
										<LazyMemberSettings member={employee} />
									</Suspense>
								) : null}
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["Member Logs"]}>
								<Suspense fallback={<div />}>
									<LazyMemberLogs />
								</Suspense>
							</ScrollTabPanel>
							<ScrollTabPanel value={value} index={TabIndex["Text Sending"]}>
								<Suspense fallback={<div />}>
									<LazySendText member={employee} />
								</Suspense>
							</ScrollTabPanel> */}
								</Box>
							</Box>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default MemberDetails;
